import { memo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import DrawIcon from '@mui/icons-material/Draw';
import SendIcon from '@mui/icons-material/Send';

function HowItWorks({ colors }) {
  const features = [
    {
      step: 1,
      icon: <AutoAwesomeIcon sx={{ fontSize: 48 }} />,
      title: 'Décrivez votre œuvre à notre agent',
      description: 'Notre agent vous guide pas à pas dans la création de vos déclarations SACEM, en extrayant automatiquement les informations de vos documents.',
      color: colors.primaryLight,
    },
    {
      step: 2,
      icon: <DescriptionIcon sx={{ fontSize: 48 }} />,
      title: 'Génération Automatique',
      description: 'Créez vos feuillets SACEM et contrats de coédition en quelques clics. Plus besoin de remplir manuellement des formulaires complexes.',
      color: colors.success,
    },
    {
      step: 3,
      icon: <DrawIcon sx={{ fontSize: 48 }} />,
      title: 'Signature Électronique',
      description: 'Envoyez vos documents pour signature électronique directement depuis la plateforme. Suivi en temps réel de l\'état des signatures.',
      color: colors.warning,
    },
    {
      step: 4,
      icon: <SendIcon sx={{ fontSize: 48 }} />,
      title: 'Envoyez vos déclarations à la SACEM',
      description: 'Transmettez vos déclarations directement à la SACEM une fois signées. Tout est centralisé au même endroit.',
      color: colors.info,
    },
  ];
  return (
        <Box id="home-features" sx={{ py: { xs: 6, md: 10 } }}>
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              fontWeight: 700,
              mb: 2,
              color: colors.textPrimary,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Comment ça marche ?
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: colors.textSecondary,
              mb: 6,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Une solution complète pour gérer toutes vos déclarations musicales en quelques minutes
          </Typography>

          <Box sx={{ maxWidth: 760, mx: 'auto', position: 'relative' }}>
            {/* Vertical timeline line */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: { xs: 22, md: 26 },
              width: 2,
              bgcolor: colors.border,
            }} />

            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  gap: { xs: 2.5, md: 3.5 },
                  pb: index === features.length - 1 ? 0 : { xs: 4, md: 5 },
                }}
              >
                {/* Numbered badge on the line */}
                <Box sx={{ flexShrink: 0, zIndex: 1 }}>
                  <Box sx={{
                    width: { xs: 46, md: 54 },
                    height: { xs: 46, md: 54 },
                    borderRadius: '50%',
                    bgcolor: colors.bgPaper,
                    border: `2px solid ${feature.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: feature.color,
                    fontWeight: 800,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    boxShadow: `0 0 0 6px ${colors.bgDefault}`,
                  }}>
                    {feature.step}
                  </Box>
                </Box>

                {/* Step content */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: colors.bgPaper,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 3,
                    p: { xs: 2.5, md: 3 },
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: feature.color,
                      transform: 'translateX(4px)',
                      boxShadow: `0 8px 20px ${feature.color}20`,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ color: feature.color, display: 'flex', '& svg': { fontSize: 28 } }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: colors.textSecondary, lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </Box>
  );
}

export default memo(HowItWorks);
